import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown } from 'lucide-react';

export default function ExperienceSection({ workExperiences }) {
  const [expandedIndex, setExpandedIndex] = useState(null);

  const container = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariant = {
    hidden: { opacity: 0, x: -30 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { type: 'spring', stiffness: 100 },
    },
  };

  return (
    <section id="experience" className="py-20 px-4 bg-gray-50 dark:bg-slate-900">
      <div className="max-w-4xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            Work Experience
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-400">
            My professional journey
          </p>
        </motion.div>

        {/* Timeline */}
        <motion.div
          variants={container}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="space-y-6"
        >
          {workExperiences.experience.map((exp, i) => (
            <motion.div
              key={i}
              variants={itemVariant}
              className="relative"
            >
              {/* Timeline dot */}
              <div className="absolute left-0 top-6 w-4 h-4 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full md:translate-x-0 -translate-x-6 border-4 border-white dark:border-slate-950" />

              {/* Timeline line */}
              {i !== workExperiences.experience.length - 1 && (
                <div className="absolute left-2 top-12 w-0.5 h-24 bg-gradient-to-b from-primary-500 to-transparent md:translate-x-0 -translate-x-3" />
              )}

              {/* Card */}
              <motion.button
                onClick={() => setExpandedIndex(expandedIndex === i ? null : i)}
                className="w-full text-left"
              >
                <div className="ml-8 bg-white dark:bg-slate-800 rounded-xl p-6 hover:shadow-lg transition-shadow duration-300 border border-gray-200 dark:border-slate-700">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                        {exp.role}
                      </h3>
                      <div className="flex items-center gap-2 text-primary-600 dark:text-primary-400 font-semibold mt-1">
                        {exp.companylogo && (
                          <img
                            src={exp.companylogo}
                            alt={exp.company}
                            className="w-8 h-8 rounded-full object-cover"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        )}
                        {exp.company}
                      </div>
                    </div>
                    <motion.div
                      animate={{ rotate: expandedIndex === i ? 180 : 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <ChevronDown size={20} className="text-gray-400" />
                    </motion.div>
                  </div>

                  {/* Date */}
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                    {exp.date}
                  </p>

                  {/* Description */}
                  <p className="text-gray-700 dark:text-gray-300 mb-3">
                    {exp.desc}
                  </p>

                  {/* Expandable content */}
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{
                      height: expandedIndex === i ? 'auto' : 0,
                      opacity: expandedIndex === i ? 1 : 0,
                    }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="pt-4 border-t border-gray-200 dark:border-slate-700 mt-4">
                      <h4 className="font-semibold text-gray-900 dark:text-white mb-3">
                        Key Achievements
                      </h4>
                      <ul className="space-y-2">
                        {exp.descBullets.map((bullet, idx) => (
                          <li
                            key={idx}
                            className="flex gap-3 text-gray-700 dark:text-gray-300"
                          >
                            <span className="text-primary-500 font-bold mt-1">•</span>
                            <span>{bullet}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </motion.div>
                </div>
              </motion.button>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
